import type { PersonSortKey, PersonSummary, Role } from '@sentra/contracts';
import { personSortKeySchema } from '@sentra/contracts';
import { createColumnHelper, flexRender, useTable } from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronFirst,
  ChevronLast,
  ChevronsUpDown,
  Download,
  UserRound,
} from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { Checkbox } from '@/design/components/checkbox';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { cn } from '@/design/utils';
import { PersonThumbnail } from '@/features/persons/images/person-thumbnail';
import type { ManualAction } from '@/features/persons/table/acknowledgment';
import { actionsFor } from '@/features/persons/table/actions';
import type { RegisterTableFeatures } from '@/features/persons/table/table-model';
import { registerTableFeatures } from '@/features/persons/table/table-model';
import type { Density, RegisterView } from '@/features/persons/table/url-state';
import { useFormatters } from '@/i18n/use-format';

/**
 * The register's table: one page of it, drawn.
 *
 * **One search box, answered by the server.** The projection is an in-memory index the api holds, and
 * it is what makes a name, half a name, a passport number or the last digits of a telephone all
 * findable from one box. A per-column filter over those values would have to decrypt every record in
 * the installation to answer one page, which is the opposite of what the projection exists for.
 *
 * **Every narrowing and the ordering are the SERVER's, and there is only one kind now.** `status`,
 * `country`, presence and whether a photograph is on file are all applied over everybody before the
 * answer is counted and cut into pages, and the column headers ask the server to reorder rather than
 * reordering what is here. The distinction that used to be drawn on this screen is gone because the
 * weaker half of it is: a filter over the rows that came back is a statement about those rows
 * dressed up as a statement about the register, and reading the first as the second is how "no
 * matches" gets believed about somebody who is registered.
 *
 * **The pager is the server's numbers.** `page` of `pageCount`, and `total` beside them, so what the
 * operator reads describes the whole register rather than one response. While a page is being
 * fetched the one on screen stays, marked `aria-busy`, with the pager disabled: a table that
 * unmounts on every press of Next is a table nobody can page through.
 *
 * **A row action opens the acknowledgment first** (AD-53), and the actions offered are the ones the
 * role holds the verb for, read from the single grant table in `@sentra/contracts`.
 *
 * **The export is of the page on screen, and it says so.** Every value in it has already been served
 * to this browser and rendered in front of the person pressing the button. It used to promise the
 * whole filtered set, which one page of twenty cannot keep; the whole-register extract is the
 * administration export, which is audited and carries a password.
 */

/**
 * What each density actually changes, as literal Tailwind classes.
 *
 * **A row's `height` cannot compact a table row, which is why this replaced one.** CSS 2.1 §17.5.3
 * makes the used height of a row the MAXIMUM of its specified height and its content, so `height` is
 * a floor and never a ceiling. The photograph cell is `size-10` inside `p-2` — 40 + 8 + 8 = 56px,
 * which is exactly what `h-14` was asking for — so "normal" requested the height the content already
 * had and "compact" requested less and was ignored. In the default view the control moved zero
 * pixels. (With the photograph column hidden it did work, because the tallest remaining cell is a
 * 24px button: that is why it looked functional to anybody who tested it that way.)
 *
 * The two levers that DO compact a row are the cell's padding and the size of its tallest content,
 * so density drives both. Compact lands at 32 + 4 + 4 = 40px against comfortable's 56, and nothing
 * else in the row exceeds it: a text cell is one 20px line plus padding, and the actions cell is an
 * `h-6` button plus padding.
 *
 * **The class strings are literal on purpose.** Tailwind 4 scans source TEXT for class names, so a
 * constructed `py-${n}` emits no rule at all and the whole control would be inert again, in exactly
 * the way it was inert before — silently, and invisibly to every gate, because no test in this
 * repository renders CSS.
 */
const ROW_DENSITY = {
  comfortable: { cell: 'px-2 py-2', avatar: 'size-10' },
  compact: { cell: 'px-2 py-1', avatar: 'size-8' },
} as const satisfies Record<Density, { readonly cell: string; readonly avatar: string }>;

const columnHelper = createColumnHelper<RegisterTableFeatures, PersonSummary>();

/** The column ids, in one place, because the URL carries them and a rename would break a link. */
export const COLUMN = {
  shortRef: 'shortRef',
  referenceCode: 'referenceCode',
  photo: 'photo',
  displayName: 'displayName',
  status: 'status',
  presence: 'presenceState',
  lastActivity: 'lastActivityAt',
  actions: 'actions',
} as const;

/**
 * The columns an operator may hide.
 *
 * The reference and the actions are not among them: a table with no reference column identifies
 * nobody, and one with no actions is a list somebody cannot act on, which reads as a broken screen
 * rather than as a choice they made.
 */
export const HIDEABLE_COLUMNS = [
  COLUMN.referenceCode,
  COLUMN.photo,
  COLUMN.displayName,
  COLUMN.status,
  COLUMN.presence,
  COLUMN.lastActivity,
] as const;

export function PersonTable({
  rows,
  page,
  busy,
  view,
  role,
  onViewChange,
  onAction,
  onExport,
}: Readonly<{
  rows: readonly PersonSummary[];
  /** Where this page sits in the whole answer, as the server counted it. */
  page: { readonly page: number; readonly pageCount: number; readonly total: number };
  /** True while the rows on screen are a page kept from before the current request answered. */
  busy: boolean;
  view: RegisterView;
  role: Role;
  onViewChange: (next: RegisterView) => void;
  onAction: (action: ManualAction, person: PersonSummary) => void;
  /**
   * Export the rows on screen, which is what this table has.
   *
   * The page rather than the whole filtered set, and the button says so. A register is paged by the
   * server now, so "everything that matches" is not in this browser and could only be assembled by
   * asking for every page of it — a bulk extract from an operator's terminal, which is what the
   * administration export exists for and is audited as.
   */
  onExport: (rows: readonly PersonSummary[]) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const format = useFormatters();
  const offered = useMemo(() => actionsFor(role), [role]);

  // The photograph is the tallest thing in a row, so it is what actually decides the row's height:
  // shrinking the padding without shrinking this would leave compact at 56px, which is the bug this
  // change exists to fix, one layer down.
  const avatar = ROW_DENSITY[view.density].avatar;

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('shortRef', {
          id: COLUMN.shortRef,
          header: t('person.table.columnRef'),
          cell: (info) => (
            <span className="font-mono" data-numeric>
              {info.getValue()}
            </span>
          ),
        }),
        columnHelper.accessor('referenceCode', {
          id: COLUMN.referenceCode,
          header: t('person.table.columnReference'),
          cell: (info) => {
            const reference = info.getValue();
            return reference === null ? (
              // An em dash rather than an empty cell: a blank reads as a rendering fault, where
              // "this record has none" is a fact an operator needs during the week the bands are
              // still in transit.
              <span className="text-muted-foreground">—</span>
            ) : (
              <span className="font-mono" data-numeric>
                {reference}
              </span>
            );
          },
        }),
        columnHelper.accessor((row) => (row.primaryImageId === null ? 'without' : 'with'), {
          id: COLUMN.photo,
          header: t('person.table.columnPhoto'),
          enableSorting: false,
          cell: (info) => {
            const person = info.row.original;
            return person.primaryImageId === null ? (
              // **`role="img"`, because `aria-label` is prohibited on a bare `span`.** The icon
              // inside is `aria-hidden`, so without a name this cell announces nothing at all — and
              // with a name and no role it is a serious axe finding rather than a label. The role is
              // also true: this is the slot a photograph would occupy, standing in for one.
              <span
                role="img"
                aria-label={t('person.table.noPhoto')}
                className={cn(
                  avatar,
                  'flex items-center justify-center rounded-md bg-muted text-muted-foreground',
                )}
              >
                <UserRound aria-hidden className="size-4" />
              </span>
            ) : (
              // **A page draws one of these per row**, and they arrive together as ONE batched
              // request rather than a request each: twenty requests per page turn was a register
              // competing with the gate for the address budget the whole building shares. The batch
              // also takes its server-side slots below the reserve the gate's own face is read from.
              <PersonThumbnail
                imageId={person.primaryImageId}
                alt={t('person.table.photoAlt', { shortRef: person.shortRef })}
                className={cn(avatar, 'rounded-md object-cover')}
              />
            );
          },
        }),
        columnHelper.accessor('displayName', {
          id: COLUMN.displayName,
          header: t('person.table.columnName'),
        }),
        columnHelper.accessor('status', {
          id: COLUMN.status,
          header: t('person.table.columnStatus'),
          cell: (info) => t(`person.table.status_${info.getValue()}`),
        }),
        columnHelper.accessor('presenceState', {
          id: COLUMN.presence,
          header: t('person.table.columnPresence'),
          cell: (info) => t(`person.table.presence_${info.getValue()}`),
        }),
        columnHelper.accessor('lastActivityAt', {
          id: COLUMN.lastActivity,
          header: t('person.table.columnLastActivity'),
          cell: (info) => {
            const value = info.getValue();
            return value === null ? t('person.table.never') : format.dateTime(value);
          },
        }),
        columnHelper.display({
          id: COLUMN.actions,
          header: t('person.table.columnActions'),
          cell: (info) => (
            <RowActions offered={offered} person={info.row.original} onAction={onAction} />
          ),
        }),
      ]),
    [avatar, format, offered, onAction, t],
  );

  const columnVisibility = useMemo(
    () => Object.fromEntries(view.hidden.map((id) => [id, false])),
    [view.hidden],
  );
  const sorting = useMemo(
    () => (view.sort === null ? [] : [{ id: view.sort.columnId, desc: view.sort.descending }]),
    [view.sort],
  );

  const table = useTable({
    features: registerTableFeatures,
    columns,
    data: rows,
    getRowId: (row) => row.id,
    // **The rows are handed back in the order they arrived.** The server ordered them over the
    // whole register; a second sort here would reorder twenty of them under a page count that
    // describes all of them.
    manualSorting: true,
    state: { columnVisibility, sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      const columnId = first === undefined ? null : sortKeyOf(first.id);
      onViewChange({
        ...view,
        sort: columnId === null ? null : { columnId, descending: first?.desc === true },
        // A new ordering is a new list, and page four of the old one means nothing in it.
        page: 1,
      });
    },
  });

  const shown = table.getRowModel().rows;
  // No `Math.max` guard: this table is rendered only when the page holds rows, which makes
  // `pageCount` at least one. A floor here would be a branch nothing can reach.
  const pages = page.pageCount;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="register-density">{t('person.table.density')}</Label>
          <NativeSelect
            id="register-density"
            value={view.density}
            onChange={(event) => {
              onViewChange({
                ...view,
                density: event.target.value === 'compact' ? 'compact' : 'comfortable',
              });
            }}
          >
            <option value="comfortable">{t('person.table.densityComfortable')}</option>
            <option value="compact">{t('person.table.densityCompact')}</option>
          </NativeSelect>
        </div>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-sm font-medium">{t('person.table.columns')}</legend>
          <div className="flex flex-wrap gap-3">
            {HIDEABLE_COLUMNS.map((id) => (
              <label key={id} className="flex items-center gap-1.5 text-sm">
                <Checkbox
                  checked={!view.hidden.includes(id)}
                  onChange={(event) => {
                    onViewChange({
                      ...view,
                      hidden: event.target.checked
                        ? view.hidden.filter((held) => held !== id)
                        : [...view.hidden, id],
                    });
                  }}
                />
                <span>{t(`person.table.column_${id}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onExport(table.getRowModel().rows.map((row) => row.original));
          }}
        >
          <Download aria-hidden />
          {t('person.table.exportCsv')}
        </Button>
      </div>

      {/*
        A scroll container of its own, the way `features/admin/data-table.tsx` wraps its table: a
        register of real names is wider than a narrow terminal, and a page that scrolls sideways is
        a page whose row actions are off the edge of it.
      */}
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse text-sm"
          data-testid="person-table"
          aria-busy={busy}
        >
          <caption className="sr-only">{t('person.table.caption')}</caption>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="border-b text-left">
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cn(ROW_DENSITY[view.density].cell, 'font-medium')}
                    // Announced rather than drawn: the arrow beside the label is `aria-hidden`, so
                    // without this a screen reader is told a column is sortable and never which way it
                    // is sorted.
                    {...ariaSort(header.column.getCanSort(), header.column.getIsSorted())}
                  >
                    {header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortIndicator direction={header.column.getIsSorted()} />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.id} data-row={row.original.shortRef} className="border-b">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={cn(ROW_DENSITY[view.density].cell, 'align-middle')}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        {/*
          Disabled while a page is being fetched as well as at the ends, so a second press cannot
          ask for a third page while the second is still on its way.
        */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="page-first"
          disabled={busy || page.page <= 1}
          onClick={() => {
            onViewChange({ ...view, page: 1 });
          }}
        >
          <ChevronFirst aria-hidden />
          {t('person.table.firstPage')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || page.page <= 1}
          onClick={() => {
            onViewChange({ ...view, page: page.page - 1 });
          }}
        >
          {t('person.table.previousPage')}
        </Button>
        {/*
          Announced, because pressing Next changes twenty rows and otherwise says nothing: a screen
          reader is told the table is busy and never told where it landed.
        */}
        <span data-testid="page-indicator" aria-live="polite">
          {t('person.table.pageOf', { page: page.page, pages })}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || page.page >= page.pageCount}
          onClick={() => {
            onViewChange({ ...view, page: page.page + 1 });
          }}
        >
          {t('person.table.nextPage')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="page-last"
          disabled={busy || page.page >= page.pageCount}
          onClick={() => {
            onViewChange({ ...view, page: page.pageCount });
          }}
        >
          {t('person.table.lastPage')}
          <ChevronLast aria-hidden />
        </Button>
        {/*
          The whole register, or the whole of whatever was asked for — not the page. It is the
          figure an operator reads to know how big the thing they are paging through is, and it is
          the server's own count over everybody rather than a length of this array.
        */}
        <span data-testid="register-total">
          {t('person.table.total', { total: format.integer(page.total) })}
        </span>
      </div>
    </div>
  );
}

/** The contract's name for a column the register can be ordered by, or `null` for one it cannot. */
function sortKeyOf(columnId: string): PersonSortKey | null {
  const parsed = personSortKeySchema.safeParse(columnId);
  return parsed.success ? parsed.data : null;
}

/**
 * The actions offered on one row.
 *
 * A component rather than JSX inside the column definition, because a cell renderer inside a
 * `useMemo` inside a component is already three functions deep and the handler inside the map makes
 * five. What that costs is not style: a closure that deep is one nobody can read the dependencies of.
 */
function RowActions({
  offered,
  person,
  onAction,
}: Readonly<{
  offered: readonly ManualAction[];
  person: PersonSummary;
  onAction: (action: ManualAction, person: PersonSummary) => void;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex gap-1">
      {offered.map((action) => (
        <Button
          key={action}
          type="button"
          // The action as a closed-set token, for the reason the gate's direction buttons carry
          // `data-direction`: the label is prose in two languages and the verb is not.
          data-action={action}
          variant="outline"
          size="xs"
          onClick={() => {
            onAction(action, person);
          }}
        >
          {t(`person.table.action_${action}`)}
        </Button>
      ))}
    </div>
  );
}

/** The `aria-sort` a header carries, or nothing when the column does not sort. */
function ariaSort(
  sortable: boolean,
  direction: false | 'asc' | 'desc',
): { 'aria-sort'?: 'ascending' | 'descending' | 'none' } {
  if (!sortable) {
    return {};
  }
  if (direction === 'asc') {
    return { 'aria-sort': 'ascending' };
  }
  return { 'aria-sort': direction === 'desc' ? 'descending' : 'none' };
}

function SortIndicator({ direction }: Readonly<{ direction: false | 'asc' | 'desc' }>): ReactNode {
  if (direction === 'asc') {
    return <ArrowUp aria-hidden className="size-3" />;
  }
  return direction === 'desc' ? (
    <ArrowDown aria-hidden className="size-3" />
  ) : (
    <ChevronsUpDown aria-hidden className="size-3 opacity-50" />
  );
}
